import React, { useState, useMemo, useEffect } from "react";
import { apiClient } from "../api_client";

export default function PrintLabels({ id }) {
  const [format, setFormat] = useState("L7160/J8160");
  const [labelsPerPage, setLabelsPerPage] = useState(21);
  const [labelType, setLabelType] = useState("0"); // 0 = Entrant, 1 = Event Setup, 2 = Prestart
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { blob, headers } = await apiClient.printLabels.download(id, format, labelType);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition = headers.get("Content-Disposition");
      const part = contentDisposition?.split('filename="')[1];
      const filename = part ? part.split('"')[0] : "labels.pdf";

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast("Labels downloaded successfully!", "success");
    } catch (error) {
      console.error("Error downloading labels:", error);
      showToast(error.message || "Error downloading labels", "error");
    }
    setLoading(false);
  };

  const { pageWidth, pageHeight } = useMemo(() => {
    if (labelType === "1" || labelType === "2") {
      return { pageWidth: 842, pageHeight: 595 };
    }
    return { pageWidth: 595, pageHeight: 842 };
  }, [labelType]);

  const layout = useMemo(() => {
    switch (format) {
      case "L7160/J8160":
        return {
          cols: labelType === "0" ? 3 : 7,
          rows: labelType === "0" ? 7 : 3,
          leftMargin: 8,
          topMargin: 19,
          internalMargin: 10,
        };
      case "L7163/J8163":
        return {
          cols: labelType === "0" ? 2 : 7,
          rows: labelType === "0" ? 7 : 2,
          leftMargin: 8,
          topMargin: 19,
          internalMargin: 10,
        };
      case "L7565/J8565":
        return {
          cols: labelType === "0" ? 2 : 4,
          rows: labelType === "0" ? 4 : 2,
          leftMargin: 8,
          topMargin: 19,
          internalMargin: 10,
        };
      default:
        return {
          cols: 3,
          rows: 7,
          leftMargin: 8,
          topMargin: 19,
          internalMargin: 10,
        };
    }
  }, [format, labelType]);

  const { cols, rows, leftMargin, topMargin, internalMargin } = layout;

  const labelWidth = (pageWidth - 2 * leftMargin) / cols;
  const labelHeight = (pageHeight - 2 * topMargin) / rows;
  const labelsPerCalculated = cols * rows;

  useEffect(() => {
    setLabelsPerPage(labelsPerCalculated);
  }, [labelsPerCalculated]);

  const borderColor =
    labelType === "2" ? "#00ff00" : labelType === "1" ? "black" : "transparent";

  const labelStyle = {
    width: `${labelWidth}px`,
    height: `${labelHeight}px`,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: `${internalMargin}px`,
    overflow: "hidden",
    border: `2px solid ${borderColor}`,
    margin: "1px"
  };

  const fontSize = Math.max(6, 24 - Math.max(cols * 3, Math.round(rows * 1.5)));

  return (
    <div className="form-con">
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Label Type</label>
          <div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="entrant"
                value="0"
                checked={labelType === "0"}
                onChange={(e) => setLabelType(e.target.value)}
              />
              <label htmlFor="entrant" className="form-check-label">
                Entrants
              </label>
            </div>

            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="event-setup"
                value="1"
                checked={labelType === "1"}
                onChange={(e) => setLabelType(e.target.value)}
              />
              <label htmlFor="event-setup" className="form-check-label">
                Event Set Up
              </label>
            </div>

            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="prestart"
                value="2"
                checked={labelType === "2"}
                onChange={(e) => setLabelType(e.target.value)}
              />
              <label htmlFor="prestart" className="form-check-label">
                Prestart
              </label>
            </div>
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="format" className="form-label">
            Label Format
          </label>
          <select
            id="format"
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            className="form-select"
          >
            <option value="L7160/J8160">Avery L7160/J8160 (3 × 7)</option>
            <option value="L7163/J8163">Avery L7163/J8163 (2 × 7)</option>
            <option value="L7565/J8565">Avery L7565/J8565 (2 × 4)</option>
          </select>
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? (
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
          ) : (
            "Download Labels PDF"
          )}
        </button>
      </form>

      <h3 className="mt-4">Preview ({labelsPerCalculated} labels):</h3>

      <div
        style={{
          position: "relative",
          width: `${pageWidth}px`,
          height: `${pageHeight}px`,
          background: "#fff",
          marginTop: "20px",
          border: "1px solid #ccc",
          boxShadow: "0 0 10px rgba(0,0,0,0.1)",
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, ${labelWidth}px)`,
          gridTemplateRows: `repeat(${rows}, ${labelHeight}px)`,
          paddingTop: `${topMargin}px`,
          paddingLeft: `${leftMargin}px`,
          gap: "0px",
          transform: labelType === "0" ? "rotate(0deg)" : "rotate(0deg)",
          transition: "width 0.3s, height 0.3s, grid-template-columns 0.3s",
        }}
      >
        {Array.from({ length: labelsPerPage }).map((_, index) => (
          <div key={index} style={labelStyle}>
            {index === 0 ? (
              <div>
                <div style={{ fontSize, fontWeight: "bold" }}>Event Name</div>
                <div style={{ fontSize }}>YY-MM-DD</div>
                <div style={{ fontSize }}>Avery format: {format}</div>
                <div style={{ fontSize }}>
                  {labelType === "0"
                    ? "Entrants"
                    : labelType === "1"
                    ? "Event setup"
                    : "Prestart"}
                </div>
              </div>
            ) : labelType === "0" ? (
              <div>
                <div style={{ fontSize, fontWeight: "bold" }}>Entrant Name</div>
                <div style={{ fontSize }}>Team Name</div>
                <div style={{ fontSize }}>Category: Category</div>
                <div>
                  <div style={{ fontSize }}>Race Number: XX</div>
                  <div style={{ fontSize }}>PIN: XXXXXX</div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  width: "100%",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize, fontWeight: "bold", marginBottom: "10px" }}>
                  Event Name
                </div>
                <div style={{ fontSize, fontWeight: "bold", marginBottom: "10px" }}>
                  Code:
                </div>
                <div style={{ fontSize, fontWeight: "bold", marginBottom: "10px" }}>
                  XXXXXX
                </div>
                <div
                  style={{
                    width: `${labelWidth - 2 * internalMargin}px`,
                    height: `${labelWidth - 2 * internalMargin}px`,
                    border: "1px solid #000",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                  }}
                >
                  QR
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
